import { ReactNode, useState } from "react";
import BoxIcon from "src/assets/icons/SsqTitleIcon/box.svg?react";
import CutleryIcon from "src/assets/icons/SsqTitleIcon/cutlery.svg?react";
import MarketIcon from "src/assets/icons/SsqTitleIcon/market.svg?react";
// import ProductionIcon from "src/assets/icons/SsqTitleIcon/production.svg?react";
import BigBox from "src/assets/icons/SsqTitleIcon/big-box.svg?react";
// import BigProduction from "src/assets/icons/SsqTitleIcon/big-production.svg?react";
import BigMarket from "src/assets/icons/SsqTitleIcon/big-market.svg?react";
import BigCutlery from "src/assets/icons/SsqTitleIcon/big-cutlery.svg?react";

import { useTranslation } from "react-i18next";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { ButtonTabs, FoodsIcon } from "@foodpilot/foods";
import { Product } from "src/api/client-api/products/useGetOneProduct";
import { useNavigate, useLocation } from "react-router";
import { GenericBox } from "src/components/GenericBox";
import CreateOutlined from "@mui/icons-material/CreateOutlined";
import { ProductInformationSummary } from "src/components/Summary/ProductSummary/Information";
import { ProductRecipeSummary } from "src/components/Summary/ProductSummary/Recipe";
// import { ProductTransformationSummary } from "src/components/Summary/ProductSummary/Production";
import { ProductDistributionSummary } from "src/components/Summary/ProductSummary/Distribution";
import { ProductConsumptionSummary } from "src/components/Summary/ProductSummary/Consumption";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
// import { CiteoBlock } from "src/components/Partnership/CiteoBlock";
import { CiteoBlock } from "src/components/Partnership/CiteoBlock";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { ProductPackagingsSummary } from "src/components/Summary/ProductSummary/Packaging";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { ProductInformationsV2 } from "src/pages/Product/v2/ProductInformationsV2/ProductInformationsV2";

type ModifyButtonProps = {
    product: Product;
    pageNumber: number;
};
const ModifyButton = (props: ModifyButtonProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const { selectedCompany } = useFoodpilotContext();
    const { product, pageNumber } = props;

    const modifyText = t("common.actions.edit", "Modify");

    if (product.canModify === false) {
        return null;
    }

    // Déterminer si c'est un produit intermédiaire pour utiliser les bonnes URLs
    const isIntermediateProduct = product.isIntermediate === true;
    const urlHandlerRoutes = isIntermediateProduct ? URLHandler.intermediateProduct : URLHandler.product;

    return (
        <Button
            variant="primary"
            startIcon={<CreateOutlined />}
            sx={{ marginRight: "24px", borderRadius: "44px" }}
            onClick={() => {
                if (selectedCompany === null) return;
                const currentUrl = location.pathname + location.search;
                const editUrl = urlHandlerRoutes.edit(selectedCompany.id, product.id, pageNumber);
                navigate(`${editUrl}?returnUrl=${encodeURIComponent(currentUrl)}`);
            }}
        >
            {modifyText}
        </Button>
    );
};

type Page = {
    title: string;
    icon?: ReactNode;
    content: ReactNode | ReactNode[];
};

export const ProductInformations = () => {
    const isProductUxV2 = useIsProductUxV2();
    if (isProductUxV2) {
        return <ProductInformationsV2 />;
    }
    return <ProductInformationsV1 />;
};

const ProductInformationsV1 = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { product, productReference } = useProductMetadataContext();
    const { isFeatureActivated } = useApplicationParameters();
    const isProductConsumptionEnabled = isFeatureActivated("FS-product-consumption");
    const isProductDistributionEnabled = isFeatureActivated("FS-product-distribution");
    const [currentPage, setCurrentPage] = useState(0);
    const distributionFormPageIndex = 3;
    const consumptionFormPageIndex = isProductDistributionEnabled ? 4 : 3;

    const acc: Set<string> = new Set();
    const productSuppliersName =
        product.productIngredients?.reduce((previousValue, currentValue) => {
            const names = currentValue.ingredient?.suppliers?.map((i) => i.supplier.name) ?? [];
            names.forEach((name) => previousValue.add(name));

            return previousValue;
        }, acc) ?? new Set();
    const sortedSuppliersName = Array.from(productSuppliersName).toSorted();

    const allPages: Page[] = [
        {
            title: t("common.general_information", "General information"),
            icon: <FoodsIcon icon="information" size={2} />,
            content: (
                <GenericBox
                    headerIcon={<FoodsIcon icon="information" size={5} />}
                    headerText={t("common.general_information", "General information")}
                    controls={<ModifyButton product={product} pageNumber={0} />}
                >
                    <ProductInformationSummary
                        title={product.title}
                        clientId={product.clientId}
                        productLine={product.productLine?.name}
                        productReference={productReference}
                        brandName={product.brand?.name}
                        netWeight={product.netWeight}
                        organic={product.organic}
                        tags={product.tags ? Object.values(product.tags).flat() : []}
                    />
                </GenericBox>
            ),
        },
        {
            title: t("product.recipe.title", "Recipe"),
            icon: <FoodsIcon icon="recipe" size={2} />,
            content: (
                <GenericBox
                    headerIcon={<FoodsIcon icon="recipe" size={5} />}
                    headerText={t("product.recipe.title", "Recipe")}
                    controls={<ModifyButton product={product} pageNumber={1} />}
                >
                    <ProductRecipeSummary
                        productIngredients={product.productIngredients ?? []}
                        suppliersName={sortedSuppliersName}
                    />
                </GenericBox>
            ),
        },
        {
            title: t("product.packagings.title", "Packagings"),
            icon: <BoxIcon />,
            content: (
                <Stack display={"flex"} flexDirection={"column"} gap={"24px"}>
                    <GenericBox
                        headerIcon={<BigBox />}
                        headerText={t("product.packagings.title", "Packagings")}
                        controls={<ModifyButton product={product} pageNumber={2} />}
                    >
                        <ProductPackagingsSummary productPackagings={product.productPackagings ?? []} />
                    </GenericBox>
                    <CiteoBlock />
                </Stack>
            ),
        },
        ...(isProductDistributionEnabled ?
            [
                {
                    title: t("product.distribution.title", "Distribution"),
                    icon: <MarketIcon />,
                    content: (
                        <GenericBox
                            headerIcon={<BigMarket />}
                            headerText={t("product.distribution.title", "Distribution")}
                            controls={<ModifyButton product={product} pageNumber={distributionFormPageIndex} />}
                        >
                            <ProductDistributionSummary
                                distribution={product.distribution}
                                distributionReferenceId={product.distributionReferenceId}
                            />
                        </GenericBox>
                    ),
                },
            ]
        :   []),
        ...(isProductConsumptionEnabled ?
            [
                {
                    title: t("product.consumption.title", "Consumption"),
                    icon: <CutleryIcon />,
                    content: (
                        <GenericBox
                            headerIcon={<BigCutlery />}
                            headerText={t("product.consumption.title", "Consumption")}
                            controls={<ModifyButton product={product} pageNumber={consumptionFormPageIndex} />}
                        >
                            <ProductConsumptionSummary productConsumptions={product.productConsumptions ?? []} />
                        </GenericBox>
                    ),
                },
            ]
        :   []),
    ];

    const choices = allPages.map((p) => (
        <Stack key={p.title} direction="row" alignItems="center" gap="8px">
            {p.icon}
            {p.title}
        </Stack>
    ));

    return (
        <Stack padding={theme.spacing(2, 0)} gap={theme.spacing(2)}>
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.PRODUCT_DETAIL_INFORMATION_HEADER} />
            <Typography variant={"h2"}>{t("product.information.title", "Product information")}</Typography>
            <Stack>
                <ButtonTabs choices={choices} onClick={(choiceIndex) => setCurrentPage(choiceIndex)} />
            </Stack>
            {allPages[currentPage].content}
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.PRODUCT_DETAIL_INFORMATION_FOOTER} />
        </Stack>
    );
};
